import { useState, useRef, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { UserRole } from '@/types'
import { getRoleDisplayName, getRoleBadgeColor } from '@/utils/permissions'
import { ChevronDown, Eye, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

const AVAILABLE_ROLES: UserRole[] = ['super_admin', 'admin', 'manager', 'client']

/**
 * Role switcher dropdown for super admins to view the app as other roles
 * Only visible to super admins
 */
export function RoleSwitcher() {
  const { user, effectiveRole, setImpersonatedRole, isImpersonating } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Only show for super admins
  if (user?.role !== 'super_admin') {
    return null
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleRoleSelect = (role: UserRole) => {
    // If selecting super_admin, clear impersonation
    if (role === 'super_admin') {
      setImpersonatedRole(null)
    } else {
      setImpersonatedRole(role)
    }
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors',
          isImpersonating
            ? 'bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
        )}
      >
        <Eye className="w-4 h-4" />
        <span className="hidden sm:inline">
          {isImpersonating ? `Viewing as ${getRoleDisplayName(effectiveRole)}` : 'View as...'}
        </span>
        <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              View app as role
            </p>
          </div>
          {AVAILABLE_ROLES.map((role) => {
            const isSelected = effectiveRole === role
            const isOriginalRole = role === 'super_admin' && !isImpersonating

            return (
              <button
                key={role}
                onClick={() => handleRoleSelect(role)}
                className={cn(
                  'w-full flex items-center justify-between px-3 py-2 text-sm transition-colors',
                  isSelected
                    ? 'bg-framed-yellow-100 text-framed-yellow-700'
                    : 'text-gray-700 hover:bg-gray-50'
                )}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border',
                      getRoleBadgeColor(role)
                    )}
                  >
                    {getRoleDisplayName(role)}
                  </span>
                  {isOriginalRole && (
                    <span className="text-xs text-gray-500">(Your role)</span>
                  )}
                </div>
                {isSelected && <Check className="w-4 h-4 text-framed-yellow-600" />}
              </button>
            )
          })}
          {isImpersonating && (
            <div className="border-t border-gray-100 mt-1 pt-1">
              <button
                onClick={() => {
                  setImpersonatedRole(null)
                  setIsOpen(false)
                }}
                className="w-full px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 text-left transition-colors"
              >
                Exit impersonation
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
